import type { ElementType, HTMLAttributes, ReactNode } from 'react';

// Panel de vidrio del sistema (policies/ux-ui-design.md §3): los paneles no son planos.
// Padding por §4: `p-6` estándar, `p-8` prominente — `p-4` como padding raíz está prohibido.

export type CardPadding = 'none' | 'md' | 'lg';

const PADDING: Record<CardPadding, string> = {
  none: '',
  md: 'p-6',
  lg: 'p-8',
};

// `title` se omite del tipo nativo a propósito: el `title` de HTML es un string (el tooltip), y el
// de esta tarjeta es el ENCABEZADO, que puede llevar nodos (un badge de estado, un contador).
export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  as?: ElementType;
  padding?: CardPadding;
  title?: ReactNode;
  description?: ReactNode;
  /** Acciones del encabezado (botones, badges). */
  actions?: ReactNode;
  /** Realce sutil al pasar el mouse: para tarjetas que además son enlaces. */
  interactive?: boolean;
  children?: ReactNode;
}

export const GLASS_PANEL =
  'bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border border-white/20 dark:border-zinc-800/50 shadow-xl';

export function Card({
  as: Tag = 'section',
  padding = 'md',
  title,
  description,
  actions,
  interactive = false,
  className = '',
  children,
  ...rest
}: CardProps) {
  return (
    <Tag
      className={[
        GLASS_PANEL,
        'rounded-3xl transition-all duration-300',
        interactive ? 'hover:-translate-y-0.5 hover:shadow-2xl' : '',
        PADDING[padding],
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {(title || actions || description) && (
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            {title && (
              <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">{title}</h2>
            )}
            {description && (
              <p className="mt-1 text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">{description}</p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      {children}
    </Tag>
  );
}

/**
 * Contenedor de página. Las medidas son las de policies/ux-ui-design.md §4 y no se negocian por
 * pantalla: dos vistas con anchos distintos se leen como dos productos distintos.
 */
export function PageContainer({ className = '', children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={['w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}
